import React, { useState } from "react";
import toast from "react-hot-toast";

import {
  DialogProps,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Body1,
  DialogActions,
  DialogTrigger,
  Button,
  Spinner,
} from "@fluentui/react-components";

import { useDocumentClassification } from "@/context/document-classification";

import { Playbook } from "@/types/risk-analysis";
import log from "@/utils/logger";

export interface DeletePlaybookDialogProps extends Omit<DialogProps, "children"> {
  playbook: Playbook;
}

export function DeletePlaybookDialog({ playbook, onOpenChange, ...props }: DeletePlaybookDialogProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const { removePlaybook } = useDocumentClassification();

  const handleDeletePlaybook = async () => {
    try {
      setLoading(true);
      await removePlaybook(playbook.id);
      onOpenChange?.(undefined, {
        open: false,
        type: "triggerClick",
        event: undefined,
      });
    } catch (error) {
      toast.error(
        error?.message || "Something went wrong while trying to delete the playbook. Please try again later."
      );
      log.error("failed to delete playbook", "DeletePlaybookDialog", { playbook });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} {...props}>
      <DialogSurface aria-labelledby={`delete-${playbook.id}-title`} aria-describedby={`delete-${playbook.id}-content`}>
        <DialogBody>
          <DialogTitle id={`delete-${playbook.id}-title`}>Delete Playbook</DialogTitle>
          <DialogContent id={`delete-${playbook.id}-content`}>
            <Body1>
              Are you sure you want to delete the playbook “<b>{playbook.name}</b>”?
            </Body1>
            <br />
            <Body1>This action cannot be undone.</Body1>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button disabled={loading} appearance="secondary">
                Cancel
              </Button>
            </DialogTrigger>
            <Button
              disabled={loading}
              appearance="primary"
              icon={loading ? <Spinner size="tiny" /> : null}
              onClick={handleDeletePlaybook}
            >
              Delete
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
